import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "swiper/css";
import "swiper/css/free-mode";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { useAlert } from "react-alert";
import createMember from "../../../utility/createMember";
import FormField from "../../../components/formField/FormField";
import GreenRedOutlinedButtons from "../../../components/buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import { db } from "../../../firebase";
import Input from "../../../components/Input";
import Dropdown from "../../../components/Dropdown";
import "./organizations.scss";

import { Loader2 } from "../../../helper/loader";
import QrCodeModal from "../../../components/qrModal";
import ConfirmDeleteModal from "../../../components/deleteModal/DeleteModal";
import { SET_USER } from "../../../redux/users/action";
import ModalComponent from "../../../components/modal/Modal";
import GradientButton from "../../../components/buttons/gradientButton/GradientButton";
import PencilIcon from "../../../images/icons/pencil.png";
import { uploadMedia } from "../../../utility/uploadMedia";
import { updateData } from "../../../utility/updateData";
import * as Yup from "yup";
import defaultLogo from "../../../components/logos/logoPaddedBlackBg.png";
import { emailValidation } from "../../../helper/regexValidations";

const MAX_LOGO_SIZE = 0.5 * 1024 * 1024;

function OrganizationsPage({ organizations, orgId, setOrgId }) {
  const { t } = useTranslation("organization");
  const userStore = useSelector((state) => state.userReducer);
  const logoInputRef = useRef(null);

  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [newLogo, setNewLogo] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isNewLogoSaving, setIsNewLogoSaving] = useState(false);
  const alert = useAlert();
  const [selectedOrg, setSelectedOrg] = useState("");
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [duplicatedName, setDuplicatedName] = useState("");
  const [image, setImage] = useState(null);
  const [title, setTitle] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");
  const [legalName, setLegalName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [legalSourceOrgId, setLegalSourceOrgId] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [members, setMembers] = useState([]);
  const [memberPassword, setMemberPassword] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [currentUserToDelete, setCurrentUserToDelete] = useState(null);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const dispatch = useDispatch();

  const activity = [
    {
      id: "1",
      label: t("sportClub"),
      value: "Club de sport",
    },
    {
      id: "2",
      label: t("youthCommittee"),
      value: "Comité de Jeunesse",
    },
    {
      id: "3",
      label: t("schoolCommittee"),
      value: "Comité scolaire",
    },
    {
      id: "4",
      label: t("festivalCommittee"),
      value: "Comité des fêtes",
    },
    {
      id: "5",
      label: t("folklore"),
      value: "Folklore",
    },
    {
      id: "6",
      label: t("nonProfitOrganization"),
      value: "ASBL",
    },
    {
      id: "7",
      label: t("eventCompany"),
      value: "Entreprise",
    },
    {
      id: "8",
      label: t("other"),
      value: "Autre",
    },
  ];

  const roles = [
    {
      id: "1",
      label: t("administrator"),
      value: "Admin",
      registrable: false,
      invitable: true,
    },
    {
      id: "2",
      label: t("cashier"),
      value: "Cashier",
      registrable: true,
      invitable: true,
    },
    {
      id: "3",
      label: t("steward"),
      value: "Steward",
      registrable: false,
      invitable: true,
    },
    {
      id: "4",
      label: t("kitchen"),
      value: "Kitchen",
      registrable: true,
      invitable: false,
    },
  ];

  async function getAllOfCollectionWhere(collectionName, field, value) {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(field, "==", value));
    const querySnapshot = await getDocs(q);
    const documents = [];
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    return documents;
  }

  async function getMembersFromDb(foundOrg) {
    if (!foundOrg) {
      setMembers([]);
      return;
    }

    let membersArrayTemp = [];

    const roles = ["adminUid", "cashierUid", "stewardUid", "kitchenUid"];
    const roleLabels = {
      adminUid: "admin",
      cashierUid: "cashier",
      stewardUid: "steward",
      kitchenUid: "kitchen",
    };

    try {
      for (const role of roles) {
        // Some legacy organisations store a single UID rather than an array.
        // Ignore malformed values so one corrupted role does not block all members.
        const roleMembers = Array.isArray(foundOrg[role])
          ? foundOrg[role]
          : typeof foundOrg[role] === "string" && foundOrg[role]
            ? [foundOrg[role]]
            : [];

        const memberSnapshots = await Promise.all(
          [...new Set(roleMembers.filter((uid) => typeof uid === "string" && uid.trim()))].map(
            (uid) => getDoc(doc(db, "Store", uid)),
          ),
        );
        memberSnapshots.forEach((userDoc) => {
          if (userDoc.exists()) {
            membersArrayTemp.push({
              ...userDoc.data(),
              roleInOrg: roleLabels[role],
            });
          }
        });
      }
      setMembers(membersArrayTemp);
    } catch (error) {
      console.error(t("error.failedFetchMembers"), error);
      alert.show(t("error.failedLoadMembers"), { type: "error" });
    }
  }

  const schema = Yup.object().shape({
    title: Yup.string().trim().required("alert.titleRequired"),

    contactEmail: Yup.string()
      .trim()
      .email("alert.invalidEmail")
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "alert.invalidEmail")
      .nullable()
      .transform((value) => (value === "" ? null : value)),

    selectedActivity: Yup.string().required("alert.activityRequired"),
  });

  const saveForm = async () => {
    try {
      setLoader(true);

      const validatedData = await schema.validate(
        { title, contactEmail, selectedActivity },
        { abortEarly: true },
      );
      const orgDocRef = doc(db, "Organizations", selectedOrg.id);

      await updateDoc(orgDocRef, {
        organization: validatedData.title,
        activity: validatedData.selectedActivity,
        contactEmail: validatedData.contactEmail,
        legalName: legalName.trim(),
        vatNumber: vatNumber.trim(),
        businessAddress: businessAddress.trim(),
      });

      alert.show(t("alert.posUpdated"), { type: "success" });
    } catch (err) {
      alert.show(t(err.message), { type: "error" });
    }
    setLoader(false);
  };

  const inviteHandler = async () => {
    const inviteEmail = email.toLowerCase();
    if (inviteEmail === "") {
      alert.show(t("alert.enterEmail"), {
        type: "info",
      });
      return;
    }
    if (!emailValidation(inviteEmail)) {
      alert.show(t("alert.invalidEmail"), { type: "info" });
      return;
    }
    if (selectedRole === "") {
      alert.show(t("alert.chooseRole"), {
        type: "info",
      });
      return;
    }
    if (selectedRole === "Kitchen") {
      alert.show(t("alert.cannotInviteWithRole"), { type: "info" });
      return;
    }

    setLoader(true);
    const orgDocRef = doc(db, "Organizations", selectedOrg.id);
    const response = await getAllOfCollectionWhere("Store", "email", inviteEmail);

    if (response.length > 0) {
      const uid = response[0].uid;
      const roleUidField = `${selectedRole.toLowerCase()}Uid`;
      if (selectedOrg[roleUidField]?.includes(uid)) {
        alert.show(t("alert.userExists"), { type: "info" });
        setLoader(false);
      } else {
        let temp = [...(selectedOrg[roleUidField] || [])];
        temp.push(uid);

        try {
          await updateDoc(orgDocRef, {
            [roleUidField]: temp,
          });
          setEmail("");
          setSelectedRole("");
          alert.show(t("alert.invitationSuccess"), { type: "success" });
          setLoader(false);
        } catch (err) {
          alert.show(t("alert.somethingWentWrong"), {
            type: "error",
          });
          console.error(t("error.updatingDoc"), err);
          setLoader(false);
        }
      }
    } else {
      const roleEmailField = `${selectedRole.toLowerCase()}Email`;
      if (selectedOrg[roleEmailField]?.includes(inviteEmail)) {
        alert.show(t("alert.userExists"), { type: "info" });
        setLoader(false);
      } else {
        let temp = [...(selectedOrg[roleEmailField] || [])];
        temp.push(inviteEmail);

        try {
          await updateDoc(orgDocRef, {
            [roleEmailField]: temp,
          });
          setEmail("");
          setSelectedRole("");
          alert.show(t("alert.tempInviteSuccess"), {
            type: "success",
          });
          setLoader(false);
        } catch (err) {
          alert.show(t("alert.somethingWentWrong"), {
            type: "error",
          });
          console.error(t("error.updatingDoc"), err);
          setLoader(false);
        }
      }
    }
  };

  const createHandler = async () => {
    const inviteEmail = email.toLowerCase();

    if (inviteEmail === "") {
      alert.show(t("alert.enterEmail"), { type: "info" });
      return;
    }
    if (!emailValidation(inviteEmail)) {
      alert.show(t("alert.invalidEmail"), { type: "info" });
      return;
    }
    if (selectedRole === "") {
      alert.show(t("alert.chooseRole"), { type: "info" });
      return;
    }

    setLoader(true);
    const orgDocRef = doc(db, "Organizations", selectedOrg.id);

    const response = await getAllOfCollectionWhere("Store", "email", inviteEmail);

    if (
      (response[0]?.type === "cashier" && selectedRole !== "Cashier") ||
      (response[0]?.type === "kitchen" && selectedRole !== "Kitchen")
    ) {
      alert.show(t("alert.memberSingleRole"), { type: "info" });
    } else {
      const roleEmailField = `${selectedRole.toLowerCase()}Email`;

      if (selectedOrg[roleEmailField]?.includes(inviteEmail)) {
        alert.show(t("alert.userExists"), { type: "error" });
      } else if (selectedRole === "Cashier" || selectedRole === "Kitchen") {
        const role = selectedRole === "Cashier" ? "cashier" : "kitchen";

        if (response.length === 0) {
          if (memberPassword.length < 6) {
            alert.show(t("alert.passwordRequirement", { size: 6 }), { type: "error" });
            setLoader(false);
            return;
          }

          try {
            const data = await createMember({
              activeOrg: orgId[0],
              email: email,
              password: memberPassword,
              type: role,
              orgRole: role,
              username: email.split("@")[0],
            });

            await setDoc(
              doc(db, "Organizations", orgId[0]),
              {
                [`${role}Uid`]: [...(selectedOrg?.[`${role}Uid`] || []), data.uid],
              },
              { merge: true },
            );

            setQrValue({
              password: memberPassword,
              email: email,
              username: data?.username || email.split("@")[0],
            });
            alert.show(t("alert.accountCreatedSuccess"), { type: "success" });
          } catch (error) {
            console.log("Error creating cashier user:", {
              message: error.message,
              stack: error.stack,
            });
            alert.show(t("error.errorOccurred"), { type: "error" });
          }
        } else {
          try {
            await setDoc(
              doc(db, "Organizations", orgId[0]),
              {
                [`${role}Uid`]: [...(selectedOrg?.[`${role}Uid`] || []), response[0].uid],
              },
              { merge: true },
            );
            alert.show(t("alert.accountCreatedSuccess"), { type: "success" });
          } catch (error) {
            alert.show(t("error.errorOccurred"), { type: "error" });
          }
        }
      } else {
        let temp = [...(selectedOrg[roleEmailField] || [])];
        temp.push(inviteEmail);

        try {
          await updateDoc(orgDocRef, { [roleEmailField]: temp });
          alert.show(t("alert.tempInviteSuccess"), { type: "success" });
        } catch (err) {
          alert.show(t("alert.somethingWentWrong"), { type: "error" });
          console.error(t("error.updatingDoc"), err);
        }
      }
    }

    setMemberPassword("");
    setEmail("");
    setLoader(false);
  };

  const deleteMember = async (member) => {
    if (member.uid === selectedOrg?.uid) {
      alert.show(t("alert.founderCannotBeDeleted"), { type: "error" });
      return;
    }

    const roleToUidMapping = {
      admin: "adminUid",
      cashier: "cashierUid",
      steward: "stewardUid",
      kitchen: "kitchenUid",
    };

    const roleUids = roleToUidMapping[member.roleInOrg];

    if (!roleUids) {
      alert.show(t("alert.invalidRole"), { type: "error" });
      return;
    }

    setLoader(true);
    try {
      const orgDocRef = doc(db, "Organizations", orgId[0]);
      const updatedUids = selectedOrg[roleUids].filter((uid) => uid !== member.uid);

      await updateDoc(orgDocRef, {
        [roleUids]: updatedUids,
      });

      if (member.uid !== userStore.users.uid) {
        setMembers((prevMembers) => prevMembers.filter((m) => m.uid !== member.uid));
      }
      alert.show(t("alert.memberRemoved"), { type: "success" });
    } catch (error) {
      console.error(t("error.failedDeleteMember"), error);
      alert.show(t("alert.failedRemoveMember"), { type: "error" });
    }

    setCurrentUserToDelete(null);
    setLoader(false);
  };

  const createNewOrganization = async () => {
    if (loader) {
      return;
    }

    if (!title || !selectedActivity) {
      alert.show(t("alert.fillAllRequiredFields"), { type: "info" });
      return;
    }

    setLoader(true);
    const uid = userStore.users.uid;
    let id = new Date().valueOf().toString();
    let data = {
      id: id,
      organization: title,
      activity: selectedActivity,
      logo: image,
      createdAt: id,
      uid,
      isDataUpdated: true,
      cashierEmail: [],
      cashierUid: [],
      stewardEmail: [],
      stewardUid: [],
      adminEmail: [],
      adminUid: [uid],
      kitchenUid: [],
      liquidity: 0,
      skipExchangeActivated: false,
      slate: true,
      vivawalletActivated: true,
    };

    const orgRef = doc(db, "Organizations", id);
    const storeRef = doc(db, "Store", uid);

    try {
      await runTransaction(db, async (transaction) => {
        transaction.set(orgRef, data);

        if (!userStore.users.activeOrg) {
          transaction.update(storeRef, { activeOrg: id, orgRole: "admin" });
        }
      });

      alert.show(t("alert.orgCreatedSuccess"), { type: "success" });
      setOrgId?.([id]);

      try {
        const updatedUser = { ...userStore.users, activeOrg: id };
        dispatch(SET_USER(updatedUser));
      } catch (dispatchError) {
        console.error("Organization created but failed to update local user state:", dispatchError);
      }
    } catch (error) {
      alert.show(t("alert.failedCreateOrg"), { type: "error" });
      console.error(t("error.creatingDoc"), error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (orgId[0]) {
      const findOrg = organizations.find((org) => org.id === orgId[0]);
      if (findOrg) {
        setSelectedOrg(findOrg);
        setImage(findOrg?.logo ? findOrg.logo : defaultLogo);
        setTitle(findOrg?.organization ? findOrg.organization : "");
        setContactEmail(findOrg?.contactEmail ? findOrg.contactEmail : "");
        setSelectedActivity(findOrg?.activity ? findOrg.activity : "");
        setLegalName(findOrg?.legalName || "");
        setVatNumber(findOrg?.vatNumber || "");
        setBusinessAddress(findOrg?.businessAddress || "");
        setLegalSourceOrgId("");
        getMembersFromDb(findOrg);
      } else {
        setMembers([]);
      }
    } else {
      setSelectedOrg();
      setImage(defaultLogo);
      setTitle("");
      setContactEmail("");
      setSelectedActivity("");
      setLegalName("");
      setVatNumber("");
      setBusinessAddress("");
      setLegalSourceOrgId("");
    }
  }, [orgId, organizations]);

  const onDuplicate = () => {
    if (selectedOrg) {
      setIsDuplicateModalOpen(true);
    }
  };

  const onDelete = () => {
    const orgId = selectedOrg?.id;
    if (orgId) {
      setIsDeleteModalOpen(true);
    }
  };

  const duplicateModalMessage = (
    <div>
      <FormField
        type={"text"}
        name="duplicationName"
        onChange={(text) => setDuplicatedName(text)}
        label={t("duplicatedName")}
      />
    </div>
  );

  const getProductsByOrgId = async (orgId) => {
    const productsRef = collection(db, "products");
    const q = query(productsRef, where("orgId", "==", orgId));
    const querySnapshot = await getDocs(q);
    const products = [];
    querySnapshot.forEach((doc) => {
      products.push({ id: doc.id, ...doc.data() });
    });
    return products;
  };

  const getCategoriesByOrgId = async (orgId) => {
    const categoriesRef = collection(db, "Categories");
    const q = query(categoriesRef, where("orgId", "==", orgId));
    const querySnapshot = await getDocs(q);
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });
    return categories;
  };

  const onDuplicateConfirmation = async () => {
    const org = selectedOrg;
    if (org && duplicatedName && duplicatedName !== "") {
      setLoader(true);
      try {
        const newOrg = {
          ...org,
          id: new Date().valueOf().toString(),
          organization: duplicatedName,
          createdAt: new Date().valueOf(),
          cashierUid: [],
          stewardUid: [],
          cashierEmail: [],
          stewardEmail: [],
        };

        await setDoc(doc(db, "Organizations", newOrg.id), newOrg);
        const products = await getProductsByOrgId(org.id);

        const productsBatch = writeBatch(db);
        const timestamp = new Date().valueOf();
        products.forEach((product) => {
          const newProduct = {
            ...product,
            id: `${timestamp}_${Math.random().toString(36).substring(2, 11)}`,
            orgId: newOrg.id,
            createdAt: timestamp,
          };
          productsBatch.set(doc(db, "products", newProduct.id), newProduct);
        });
        await productsBatch.commit();

        const categories = await getCategoriesByOrgId(org.id);
        const categoriesBatch = writeBatch(db);
        categories.forEach((category) => {
          const newCategory = {
            ...category,
            orgId: newOrg.id,
            id: `${timestamp}_${Math.random().toString(36).substring(2, 11)}`,
          };
          categoriesBatch.set(doc(db, "Categories", newCategory.id), newCategory);
        });
        await categoriesBatch.commit();

        setDuplicatedName("");
        setIsDuplicateModalOpen(false);
        alert.show(t("alert.orgDuplicated"), { type: "success" });
      } catch (err) {
        console.error(err);
        alert.show(t("alert.failedDuplicateOrg"), { type: "error" });
      } finally {
        setLoader(false);
      }
    }
  };

  const onDeleteConfirmation = async () => {
    const org = selectedOrg;
    if (org) {
      try {
        setLoader(true);
        const orgRef = doc(db, "Organizations", org.id);
        await deleteDoc(orgRef);
        setIsDeleteModalOpen(false);
        alert.show(t("alert.orgDeleted"), { type: "success" });
      } catch (err) {
        alert.show(t("alert.failedDeleteOrg"), { type: "error" });
      } finally {
        setLoader(false);
      }
    }
  };

  const saveLogo = async () => {
    try {
      setIsNewLogoSaving(true);
      const logoUrl = await uploadMedia(newLogo, `logos/${selectedOrg.id}`);
      await updateData("Organizations", selectedOrg.id, { logo: logoUrl });
      alert.show(t("logoSaved"), { type: "success" });
    } catch (err) {
      alert.show(t("uploadError"), { type: "error" });
    } finally {
      if (newLogo) {
        URL.revokeObjectURL(newLogo);
        setNewLogo(null);
      }

      logoInputRef.current.value = null;
      setIsNewLogoSaving(false);
      setIsLogoModalOpen(false);
    }
  };

  const onDuplicationCancel = () => {
    setIsDuplicateModalOpen(false);
    setDuplicatedName("");
  };

  const onDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const duplicateModalActions = (
    <GreenRedOutlinedButtons
      greenName={t("duplicate")}
      redName={t("cancel")}
      redOnPress={onDuplicationCancel}
      greenOnPress={onDuplicateConfirmation}
    />
  );

  const deleteModalActions = (
    <GreenRedOutlinedButtons
      greenName={t("cancel")}
      redName={`/!\\ ${t("delete")}`}
      redOnPress={onDeleteConfirmation}
      greenOnPress={onDeleteCancel}
    />
  );

  const changePictureAction = (
    <div className="logoChangeModalContent">
      {newLogo && (
        <img
          src={newLogo}
          className="newLogo"
          alt={t("changeLogo", { name: selectedOrg?.organization })}
        />
      )}
      {isNewLogoSaving ? (
        <p className="logoLoader">{t("savingLogo")}</p>
      ) : (
        <GreenRedOutlinedButtons
          greenName={t("save")}
          redName={t("cancel")}
          redOnPress={() => closeLogoModal()}
          greenOnPress={saveLogo}
        />
      )}
    </div>
  );

  const translateOrgRole = (currentUser) => {
    let role = roles.find(
      (role) => role.value?.toLowerCase() === currentUser?.roleInOrg?.toLowerCase(),
    );

    let label = role?.label || " - ";

    if (role && currentUser?.uid === selectedOrg?.uid) {
      label += ` - ${t("founder")}`;
    }

    if (role && userStore.users?.uid === currentUser?.uid) {
      label += ` - ${t("myAccount")}`;
    }

    return label;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file.type.startsWith("image/")) {
      alert.show(t("notAnImage"), { type: "error" });
      return;
    }

    if (file.size >= MAX_LOGO_SIZE) {
      alert.show(t("alert.imageTooLarge", { maxSize: MAX_LOGO_SIZE / 1024 / 1024 }), {
        type: "error",
      });
      return;
    }

    const url = URL.createObjectURL(file);
    setNewLogo(url);
    setIsLogoModalOpen(true);
  };

  const closeLogoModal = () => {
    setIsLogoModalOpen(false);
    if (newLogo) {
      URL.revokeObjectURL(newLogo);
      setNewLogo(null);
    }
  };

  return (
    <>
      <ModalComponent
        isOpen={isDuplicateModalOpen}
        title={`${t("duplicate")} ${selectedOrg?.organization}`}
        actionsComponent={duplicateModalActions}
        hideCloseButton
        message={duplicateModalMessage}
        onClose={onDuplicationCancel}
      />
      <ModalComponent
        isOpen={isLogoModalOpen}
        title={t("changeLogo", { name: selectedOrg?.organization })}
        actionsComponent={changePictureAction}
        modalClassName="logoChangeModal"
        overlayClassName="logoChangeOverlay"
        hideCloseButton
        message={t("confirmChangeLogo")}
        onClose={closeLogoModal}
        shouldNotCloseOnOverlayClick
      />
      <ModalComponent
        isOpen={isDeleteModalOpen}
        title={`${t("delete")} ${selectedOrg?.organization}`}
        actionsComponent={deleteModalActions}
        hideCloseButton
        message={t("areYouSure")}
        onClose={onDeleteCancel}
      />
      <div className="container-fluid dashboardMain organisation row mx-0">
        <div className="organization-card">
          <div className="card themeCard">
            <div className="card-header">
              <div className="card-header-left"></div>
              <div className="card-header-right">
                {!!selectedOrg && (
                  <GreenRedOutlinedButtons
                    greenName={t("duplicate")}
                    redName={t("delete")}
                    greenOnPress={onDuplicate}
                    redOnPress={onDelete}
                  />
                )}
              </div>
            </div>
            <div className="card-body p-sm-3 pt-0 pt-sm-0">
              <div className="d-flex align-items-end orgs-author">
                <div className="orgs-logo-container">
                  {orgId[0] && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        ref={logoInputRef}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <button
                        className="org-logo-edit-btn"
                        onClick={() => logoInputRef.current.click()}
                      >
                        <img src={PencilIcon} />
                      </button>
                    </>
                  )}
                  <img src={image} alt="" className="orgs-logo" />
                </div>
                <h5 className="orgs-name">{selectedOrg?.organization || t("newSalesPoint")}</h5>
              </div>
              {orgId[0] ? (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-container">
                    <div className="form-container-update">
                      <h5 className="form-container-update-title">{t("editSalesPoint")}</h5>
                      <div className="form-container-update-field">
                        <Input
                          label={t("salesPointName")}
                          placeholder={t("enterName")}
                          id="title"
                          value={title}
                          onChange={(e) => {
                            setTitle(e.target.value);
                          }}
                        />
                      </div>
                      <div className="form-container-update-field">
                        <Input
                          label={t("contactEmailLabel")}
                          placeholder={t("contactEmailPlaceholder")}
                          id="contactEmail"
                          value={contactEmail}
                          onChange={(e) => {
                            setContactEmail(e.target.value);
                          }}
                        />
                      </div>
                      <div className="form-container-update-field">
                        <Dropdown
                          label={t("activityType")}
                          options={activity}
                          value={selectedActivity}
                          onChange={(e) => {
                            setSelectedActivity(e.value);
                          }}
                        />
                      </div>
                      <div className="form-container-update-legal">
                        <div className="form-container-update-legal-header">
                          <h6>{t("legalInformation")}</h6>
                          <Dropdown
                            placeholder={t("copyLegalInformation")}
                            options={organizations
                              .filter((organization) => organization.id !== selectedOrg?.id)
                              .map((organization) => ({
                                value: organization.id,
                                label: organization.organization,
                              }))}
                            value={legalSourceOrgId}
                            onChange={(option) => {
                              const sourceOrganization = organizations.find(
                                (organization) => organization.id === option.value,
                              );
                              if (!sourceOrganization) return;
                              setLegalSourceOrgId(option.value);
                              setLegalName(sourceOrganization.legalName || "");
                              setVatNumber(sourceOrganization.vatNumber || "");
                              setBusinessAddress(sourceOrganization.businessAddress || "");
                            }}
                          />
                        </div>
                        <div className="form-container-update-field">
                          <Input
                            label={t("legalName")}
                            placeholder={t("legalNamePlaceholder")}
                            id="legalName"
                            value={legalName}
                            onChange={(e) => setLegalName(e.target.value)}
                          />
                        </div>
                        <div className="form-container-update-field">
                          <Input
                            label={t("vatNumber")}
                            placeholder={t("vatNumberPlaceholder")}
                            id="vatNumber"
                            value={vatNumber}
                            onChange={(e) => setVatNumber(e.target.value)}
                          />
                        </div>
                        <div className="form-container-update-field">
                          <Input
                            label={t("businessAddress")}
                            placeholder={t("businessAddressPlaceholder")}
                            id="businessAddress"
                            value={businessAddress}
                            onChange={(e) => setBusinessAddress(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-container-add-members">
                      <h5 className="form-container-add-members-title">{t("addMembers")}</h5>
                      <p className="form-container-add-members-description">{t("allowMembers")}</p>
                      <div className="form-container-add-members-field">
                        <Input
                          label={t("email")}
                          placeholder={t("enterEmail")}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                          }}
                        />
                      </div>
                      {(selectedRole === "Cashier" || selectedRole === "Kitchen") &&
                        showPasswordField && (
                          <div className="form-container-add-members-field">
                            <Input
                              label={t("password")}
                              placeholder={t("password")}
                              value={memberPassword}
                              type="password"
                              onChange={(e) => {
                                setMemberPassword(e.target.value);
                              }}
                            />
                          </div>
                        )}
                      <div className="form-container-add-members-field">
                        <Dropdown
                          label={t("role")}
                          options={roles.filter(
                            (role) => role[showPasswordField ? "registrable" : "invitable"],
                          )}
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.value)}
                        />
                      </div>
                      <br />
                      <QrCodeModal
                        isOpen={qrValue !== ""}
                        onRequestClose={() => setQrValue("")}
                        qrValue={qrValue}
                      />
                      <GradientButton onPress={showPasswordField ? createHandler : inviteHandler}>
                        {!showPasswordField ? t("invite") : t("create")}
                      </GradientButton>
                      <button
                        className="signupBtn form-container-add-members-cashier"
                        onClick={() => {
                          setShowPasswordField(!showPasswordField);
                          setSelectedRole("Cashier");
                        }}
                      >
                        {showPasswordField ? t("cancel") : t("createMember")}
                      </button>
                    </div>
                  </div>
                  <div className="submit-button">
                    <GradientButton onPress={saveForm}>{t("save")}</GradientButton>
                  </div>
                </form>
              ) : (
                <form className="form-container-new-pos" onSubmit={(e) => e.preventDefault()}>
                  <h5 className="form-container-new-pos-title">{t("newSalesPoint")}</h5>
                  <div className="form-container-new-pos-field">
                    <Input
                      label={t("salesPointName")}
                      placeholder={t("enterName")}
                      id="title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                      }}
                      required="required"
                    />
                  </div>
                  <div className="form-container-new-pos-field">
                    <Dropdown
                      label={t("activityType")}
                      options={activity}
                      value={selectedActivity}
                      onChange={(e) => setSelectedActivity(e.value)}
                    />
                  </div>
                  <div className="form-container-new-pos-container">
                    <GradientButton onPress={createNewOrganization}>{t("create")}</GradientButton>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {orgId[0] && (
          <div className="members">
            <div className="amountMainCard themeCard right-side-bar-content members-container">
              <h5 className="subtitle members-container-title">{t("listOfMembers")}</h5>
              <div className="members-container-inner">
                {members.map((item, index) => (
                  <div
                    className="right-sidebar-history-item members-container-inner-member"
                    key={index}
                  >
                    <div className="cont members-container-inner-member-details">
                      <h6>{item?.username}</h6>
                      <p className="m-0">{translateOrgRole(item)}</p>
                      <p className="m-0">{item?.email}</p>
                    </div>
                    <div className="members-container-inner-member-details-buttons">
                      {(item?.type === "cashier" || item?.type === "kitchen") && (
                        <button
                          className="members-container-inner-member-details-buttons-qrcode"
                          onClick={() =>
                            setQrValue({
                              password: item?.password,
                              email: item?.email,
                              username: item?.username || item?.email?.split("@")[0] || "",
                            })
                          }
                        >
                          {t("seeQrCode")}
                        </button>
                      )}

                      {item.uid !== selectedOrg?.uid && (
                        <button
                          className="members-container-inner-member-details-buttons-delete"
                          onClick={() => setCurrentUserToDelete(item)}
                        >
                          <svg viewBox="0 0 26 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M18.4166 7.00008H23.8333V9.16675H21.6666V23.2501C21.6666 23.5374 21.5525 23.8129 21.3493 24.0161C21.1462 24.2193 20.8706 24.3334 20.5833 24.3334H5.41663C5.12931 24.3334 4.85376 24.2193 4.65059 24.0161C4.44743 23.8129 4.33329 23.5374 4.33329 23.2501V9.16675H2.16663V7.00008H7.58329V3.75008C7.58329 3.46276 7.69743 3.18721 7.90059 2.98405C8.10376 2.78088 8.37931 2.66675 8.66663 2.66675H17.3333C17.6206 2.66675 17.8962 2.78088 18.0993 2.98405C18.3025 3.18721 18.4166 3.46276 18.4166 3.75008V7.00008ZM9.74996 12.4167V18.9167H11.9166V12.4167H9.74996ZM14.0833 12.4167V18.9167H16.25V12.4167H14.0833ZM9.74996 4.83341V7.00008H16.25V4.83341H9.74996Z"
                              fill="#FF2F04"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <ConfirmDeleteModal
          isOpen={!!currentUserToDelete}
          onClose={() => setCurrentUserToDelete(null)}
          onConfirm={() => deleteMember(currentUserToDelete)}
        />
        {loader && <Loader2 />}
      </div>
    </>
  );
}

export { OrganizationsPage };
